import { Button, Flex, Typography } from 'antd'
import { useState } from 'react'

import { authClient, getAuthError } from '@connectors/AuthClient'
import { useNotifications } from '@store/notifications'

import { PasswordModal } from './Steps/PasswordModal'
import { QRModal } from './Steps/QRModal'

type Step = 'idle' | 'password' | 'qr' | 'disable'

export const MFAPage = () => {
    const { data: session, refetch } = authClient.useSession()
    const [step, setStep] = useState<Step>('idle')
    const [password, setPassword] = useState<string>()

    const { showNotification } = useNotifications()

    const reset = () => {
        setStep('idle')
        setPassword(undefined)
    }

    const onDisable = async (value: string) => {
        try {
            await authClient.twoFactor.disable({ password: value }, { throw: true })
        } catch (err) {
            if (getAuthError(err)?.code === 'INVALID_PASSWORD') {
                showNotification({
                    title: 'Error disabling MFA',
                    description: 'Please check your password and try again',
                    type: 'error'
                })
                return
            }

            showNotification({
                title: 'Error disabling MFA',
                description: 'Please try again',
                type: 'error'
            })
            return
        }

        reset()
        await refetch()
        showNotification({
            title: 'MFA has been disabled.',
            description: 'You will no longer be asked for a code when you log in.',
            type: 'success'
        })
    }

    return (
        <Flex gap="middle" vertical>
            <Typography.Title>MFA Setup</Typography.Title>
            {session?.user.twoFactorEnabled ? (
                <>
                    <Typography.Title level={5}>MFA is already enabled for your account</Typography.Title>
                    <Button onClick={() => setStep('disable')} style={{ width: '300px' }} danger>
                        Disable MFA
                    </Button>
                </>
            ) : (
                <Button onClick={() => setStep('password')} style={{ width: '300px' }} type="primary">
                    Enable MFA
                </Button>
            )}
            {step === 'password' && (
                <PasswordModal
                    isOpen
                    onClose={reset}
                    onSuccess={value => {
                        setPassword(value)
                        setStep('qr')
                    }}
                />
            )}
            {step === 'disable' && (
                <PasswordModal
                    isOpen
                    onClose={reset}
                    onSuccess={onDisable}
                    description="Enter your password to disable MFA."
                    submitText="Disable MFA"
                />
            )}
            {step === 'qr' && password && (
                <QRModal
                    isOpen
                    password={password}
                    onClose={reset}
                    onError={() => {
                        setPassword(undefined)
                        setStep('password')
                    }}
                    onSuccess={async () => {
                        reset()
                        await refetch()
                        showNotification({
                            title: 'MFA has been enabled successfully.',
                            description: 'You will be asked for a code the next time you log in.',
                            type: 'success'
                        })
                    }}
                />
            )}
        </Flex>
    )
}
